<template>
    <el-form class="settings-accounting-charts pt-5 px-5">
        <preloader :display="loading" class="preloader prelative" />
        <!-- CODES: DEFAULT-->
        <ul class="settings-accounting-charts--container">
            <div class="typographies-heading-h3 categories-title">
                {{ $l10n.__('Code comptable') }}
            </div>

            <!-- CODES: MAX LENGTH -->
            <li v-if="setupLang === 'fr'">
                <el-form-item>
                    <template #label>
                        <div class="typographies-heading-h4">
                            {{ $l10n.__('Nombre de caractères pour les codes') }}
                        </div>
                    </template>
                    <el-input-number
                        :value="codeLength"
                        :min="1"
                        class="wp-10"
                        :controls="false"
                        @change="updateValue('CodeLength', $event)"
                        ref="inpuSettingsAccountingLength"
                    />
                </el-form-item>
            </li>

            <!-- CODES: DEPOSIT -->
            <li>
                <el-form-item>
                    <template #label>
                        <div class="typographies-heading-h4">
                            {{ $l10n.__('Code comptable pour les acomptes') }}
                        </div>
                    </template>
                    <el-input
                        v-model="depositCode"
                        :maxlength="codeLength"
                        class="wp-10"
                        @input="updateValue('DepositCode', $event)"
                        ref="inputSettingsAccountingDeposit"
                    />
                </el-form-item>
            </li>

            <!-- CODES: THIRD/PURCHASE/BANK/SELL -->
            <li v-for="codes in labels.defaultsCodes" :key="codesCode(codes)">
                <el-form-item>
                    <template #label>
                        <div class="typographies-heading-h4">{{ codesLabel(codes) }}</div>
                    </template>
                    <el-select
                        :value="getCodeValue(codesCode(codes), codesType(codes))"
                        @change="setCodeValue($event, codesCode(codes), codesType(codes))"
                        class="wp-10"
                    >
                        <el-option
                            v-for="(accountingCode, index) in getCodesList(codesType(codes))"
                            class="wp-10"
                            :key="`third_${codesType(codes)}_${index}`"
                            :value="index"
                            :label="`${accountingCode.code} - ${accountingCode.label}`"
                        />
                    </el-select>
                </el-form-item>
            </li>
        </ul>

        <!-- CODES: VAT -->
        <ul v-if="hasVat" class="settings-accounting-charts--container">
            <div class="typographies-heading-h3 categories-title">
                {{ $l10n.__('Codes comptables TVA') }}
            </div>
            <li>
                <el-form-item v-for="codes in labels.collected_vat" :key="codesType(codes)">
                    <template #label>
                        <div class="typographies-heading-h4">
                            {{ codesLabel(codes) }}
                        </div>
                    </template>
                    <el-select
                        :value="getCodeValue(codesCode(codes), codesType(codes))"
                        @change="setCodeValue($event, codesCode(codes), codesType(codes))"
                        class="wp-10"
                    >
                        <el-option
                            v-for="(accountingCode, index) in getCodesList(codesType(codes))"
                            class="wp-10"
                            :key="`collectionVat_${codesCode(codes)}_${index}`"
                            :value="index"
                            :label="`${accountingCode.code} - ${accountingCode.label}`"
                        />
                    </el-select>
                </el-form-item>
            </li>
            <li>
                <el-form-item v-for="codes in labels.deductible_vat" :key="codesType(codes)">
                    <template #label>
                        <div class="typographies-heading-h4">{{ codesLabel(codes) }}</div>
                    </template>
                    <el-select
                        :value="getCodeValue(codesCode(codes), codesType(codes))"
                        @change="setCodeValue($event, codesCode(codes), codesType(codes))"
                        class="wp-10"
                    >
                        <el-option
                            v-for="(accountingCode, index) in getCodesList(codesType(codes))"
                            class="wp-10"
                            :key="`debitVat_${codesCode(codes)}_${index}`"
                            :value="index"
                            :label="`${accountingCode.code} - ${accountingCode.label}`"
                        />
                    </el-select>
                </el-form-item>
            </li>
        </ul>
        <!-- CODES: DISCOUNT -->
        <ul class="settings-accounting-charts--container">
            <h3 class="typographies-heading-h3 categories-title">
                {{ $l10n.__('Remises') }}
            </h3>
            <el-switch
                active-color="#39CF86"
                inactive-color="#565665"
                :active-text="
                    $t.__(
                        'settings.sellsymodule.accounting.SettingsAccountingCharts.use_specific_code',
                    )
                "
                :value="discountEnable"
                @change="updateValue('DiscountEnable', $event)"
            />
            <li v-for="codes in labels.discount" :key="codesCode(codes)">
                <el-form-item>
                    <template #label>
                        <div class="typographies-heading-h4">{{ codesLabel(codes) }}</div>
                    </template>
                    <el-select
                        :value="getCodeValue(codesCode(codes), codesType(codes))"
                        :disabled="!discountEnable"
                        @change="setCodeValue($event, codesCode(codes), codesType(codes))"
                        class="wp-10"
                    >
                        <el-option
                            v-for="(accountingCode, index) in getCodesList(codesType(codes))"
                            class="wp-10"
                            :key="`discount_${codesType(codes)}_${index}`"
                            :value="index"
                            :label="`${accountingCode.code} - ${accountingCode.label}`"
                        />
                    </el-select>
                </el-form-item>
            </li>
        </ul>
        <!-- CODE: COMMISSIONS -->
        <ul class="settings-accounting-charts--container">
            <div class="typographies-heading-h3 categories-title">
                {{ $l10n.__('Commissions') }}
            </div>
            <li>
                <h4 class="typographies-heading-h4">
                    {{ codesLabel(labels.commissions[0]) }}
                </h4>
                <el-select
                    :value="
                        getCodeValue(
                            codesCode(labels.commissions[0]),
                            codesType(labels.commissions[0]),
                        )
                    "
                    @change="
                        setCodeValue(
                            $event,
                            codesCode(labels.commissions[0]),
                            codesType(labels.commissions[0]),
                        )
                    "
                    class="wp-10"
                >
                    <el-option
                        v-for="(accountingCode, index) in getCodesList(
                            codesType(labels.commissions[0]),
                        )"
                        class="wp-10"
                        :key="`commissions_${codesType(labels.commissions[0])}_${index}`"
                        :value="index"
                        :label="`${accountingCode.code} - ${accountingCode.label}`"
                    />
                </el-select>
            </li>
        </ul>
    </el-form>
</template>

<script>
import Preloader from 'vcomponents/utilities/Preloader.vue';
import { createNamespacedHelpers } from 'vuex';
import settingsAccountingChartsModule from 'stores/accounting/settingsAccountingCharts';
const { mapState } = createNamespacedHelpers('corp');

export default {
    components: {
        Preloader,
    },
    data() {
        return {
            currentStore: 'settingsAccountingCharts',
            store: settingsAccountingChartsModule,
            depositCode: '',
            loading: true,
        };
    },
    created() {
        //Avoid duplication of store
        if (!this.$store.state.settingsAccountingCharts?.id) {
            this.$store.registerModule(this.currentStore, this.store);
            this.$store.commit(`${this.currentStore}/setAccountingChartsLabels`);

            this.$store.dispatch(`${this.currentStore}/fetchAccountingCodes`).then((codes) => {
                this.$store.dispatch(`${this.currentStore}/createVatList`, {
                    lang: this.setupLang,
                    codes: codes,
                });
                this.$store.dispatch(`${this.currentStore}/fetchUserAccountingCodes`).then(() => {
                    this.depositCode = this.$store.getters[`${this.currentStore}/getDepositCode`];
                    this.loading = false;
                });
            });
        }
    },
    beforeDestroy() {
        this.$store.unregisterModule(this.currentStore);
    },
    computed: {
        ...mapState({
            hasVat: 'hasVat',
            setupLang: 'setupLang',
        }),
        rules() {
            return this.$store.getters[`${this.currentStore}/getRules`];
        },
        labels() {
            return this.$store.getters[`${this.currentStore}/getLabels`];
        },
        discountEnable() {
            return this.$store.getters[`${this.currentStore}/getDiscountEnable`];
        },
        codeLength() {
            return this.$store.getters[`${this.currentStore}/getCodeLength`];
        },
    },
    methods: {
        updateValue: _.debounce(function (setPath, newValue) {
            this.$store.dispatch(`${this.currentStore}/updateValue`, {
                path: setPath,
                value: newValue,
            });
        }, 200),
        setCodeValue(index, code, type) {
            this.$store.dispatch(`${this.currentStore}/updateCode`, {
                code: code,
                type: type,
                index: index,
            });
        },
        getCodeValue(code, type) {
            const accountingCode = this.$store.getters[`${this.currentStore}/getCode`]({
                code: code,
                type: type,
            });
            if (!accountingCode?.value?.code && !accountingCode?.value?.label) {
                return null;
            }
            return `${accountingCode?.value?.code || ''} - ${accountingCode?.value?.label || ''}`;
        },
        getCodesList(type) {
            return this.$store.getters[`${this.currentStore}/getCodesList`](type) || [];
        },
        codesCode(mainType) {
            return !_.isUndefined(mainType) ? mainType.code : null;
        },
        codesType(mainType) {
            return !_.isUndefined(mainType) ? mainType.type : null;
        },
        codesLabel(mainType) {
            return !_.isUndefined(mainType) ? mainType.label : null;
        },
    },
};
</script>

<style lang="scss">
@import '~@sellsy/styleguide/src/_variables';

.settings-accounting-charts {
    margin-bottom: 80px;
    background: $white;
    border: 1px solid #ccccd1;
    border-radius: 4px;
    max-width: 1040px;
    padding-bottom: 40px;
    &--title {
        margin-bottom: var(--spacing-5);
    }
    .categories-title {
        margin-bottom: var(--spacing-3);
    }
    &--container {
        h3,
        h4 {
            margin: 0;
        }
        & > li {
            display: flex;
            flex-direction: column;
            width: 500px;
            h4 {
                margin-bottom: var(--spacing-0);
            }
            .el-form-item > label {
                margin-bottom: var(--spacing-0);
                color: var(--colors-common-text-standard-default);
            }
        }

        .el-switch {
            margin-bottom: var(--spacing-3);
            .el-switch__core {
                width: 36px !important;
                background: $gray-80;
            }
            .el-switch__label {
                margin-left: 8px;
                display: flex;
                font-weight: var(--font-weights-proxima-nova-1);
                color: var(--colors-common-text-standard-default);
                & > span {
                    align-self: center;
                }
            }
        }
        .input-with-select.el-input.el-input-group {
            .el-input-group__append {
                background-color: $white;
                color: $gray-100;
            }
        }
        .el-input-number input {
            text-align: left;
        }
        &:not(&:last-child) {
            border-bottom: solid var(--colors-common-border-default) 1px;
            margin-top: 0;
            margin-bottom: var(--spacing-5);
            padding-bottom: var(--spacing-5);
        }
    }
}
.el-popper.discount-dropdown {
    margin-top: 0px !important;
    .popper__arrow {
        display: none !important;
    }
}
</style>
